// TypeScript mirror of the universal hotel contract documented in
// `hotels/schemas.py` on the backend. Keep field names verbatim — the API
// never wraps responses, it returns these shapes directly.

import type { BookingStatus } from "./types";

// Which upstream serves a search. Sent as `source` on /v1/hotels/search/ and
// echoed back as `provider` on every response. Everything after the search is
// pinned to whichever provider served it.
export type HotelSource = "nuitee" | "travclan";

export const HOTEL_SOURCES: Array<{ value: HotelSource; label: string }> = [
  { value: "nuitee", label: "Nuitee" },
  { value: "travclan", label: "TravClan" },
];

export const DEFAULT_HOTEL_SOURCE: HotelSource = "nuitee";

export type BoardBasis =
  | "room_only"
  | "bed_and_breakfast"
  | "half_board"
  | "full_board"
  | "all_inclusive";

/** ONE ROOM. `children` is a list of ages, not a count — rates are age-priced. */
export interface Occupancy {
  adults: number;
  children?: number[];
}

export interface GuestCount {
  /** How many rooms — and therefore how many guest names to collect. */
  rooms: number;
  adults: number;
  children: number;
}

export interface Destination {
  city_name?: string | null;
  country_code?: string | null;
  country_name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radius?: number | null;
  hotel_ids?: string[] | null;
}

export interface TaxAtProperty {
  description: string;
  amount: number;
  currency: string;
}

export interface HotelPricing {
  currency: string;
  base_fare: number;
  tax_and_surcharge: number;
  published_fare: number;
  final_fare: number;
  per_night_fare?: number | null;
  /** Collected by the PROPERTY at check-in — never part of final_fare. */
  taxes_at_property: number;
  taxes_at_property_breakdown: TaxAtProperty[];
  previous_total_amount?: number | null;
  price_changed?: boolean;
}

export interface CancellationPolicy {
  deadline?: string | null;
  amount?: number | null;
  currency: string;
  type?: string | null;
  timezone?: string | null;
}

export interface HotelImage {
  url: string;
  caption?: string | null;
}

export interface HotelCard {
  hotel_id: string;
  name: string;
  address?: string | null;
  city_name?: string | null;
  country_code?: string | null;
  coordinates: { latitude?: number | null; longitude?: number | null };
  /** null means UNRATED, never zero stars — the provider uses 0 as a sentinel. */
  star_rating?: number | null;
  /** Provider scale (0-10). null means unscored. */
  review_score?: number | null;
  /** Same score on a 5-point scale. */
  review_rating?: number | null;
  review_count?: number | null;
  thumbnail?: string | null;
  main_image?: string | null;
  facilities: string[];
  board_type: string;
  board_basis: BoardBasis | string;
  board_name: string;
  free_breakfast: boolean;
  refundable?: boolean | null;
  cheapest_room_name?: string | null;
  room_type_count: number;
  /** True when the property has MORE bookable room types than the detail page
   *  will show — render "40+", not "40". */
  room_types_capped?: boolean;
  pricing: HotelPricing;
  kaira_picked?: boolean;
}

export interface HotelPolicy {
  type?: string | null;
  name?: string | null;
  description?: string | null;
}

export interface HotelSentiment {
  pros: string[];
  cons: string[];
  categories: Array<{ name: string; rating?: number | null; description?: string | null }>;
}

export interface HotelDetailInfo extends HotelCard {
  description?: string | null;
  important_information?: string | null;
  images: HotelImage[];
  check_in_time?: string | null;
  check_in_end_time?: string | null;
  check_out_time?: string | null;
  phone?: string | null;
  email?: string | null;
  chain?: string | null;
  hotel_type?: string | null;
  policies: HotelPolicy[];
  sentiment?: HotelSentiment | null;
  nearby: Array<{ name: string; distance_km?: number | null }>;
}

export interface RoomRate {
  rate_id: string;
  /** 1-based. The room this rate is for, and what a guest attaches to. */
  occupancy_number: number;
  name: string;
  adults: number;
  children: number;
  children_ages: number[];
  max_occupancy?: number | null;
  board_type: string;
  board_basis: BoardBasis | string;
  board_name: string;
  free_breakfast: boolean;
  refundable?: boolean | null;
  cancellation_policies: CancellationPolicy[];
  cancellation_summary: string;
  free_cancellation_until?: string | null;
  remarks?: string | null;
  pricing: HotelPricing;
}

/**
 * One room type == one `offer_id` == one supplier call.
 * `rates[]` are the per-occupancy rows beneath it, so a two-room search returns
 * one offer carrying two rates and a `pricing` that covers both.
 */
export interface RoomOffer {
  offer_id: string;
  room_type_id?: string | null;
  mapped_room_id?: string | null;
  name: string;
  description?: string | null;
  board_type: string;
  board_basis: BoardBasis | string;
  board_name: string;
  free_breakfast: boolean;
  refundable?: boolean | null;
  cancellation_policies: CancellationPolicy[];
  cancellation_summary: string;
  free_cancellation_until?: string | null;
  rooms_count: number;
  sleeps: { adults: number; children: number };
  max_occupancy?: number | null;
  rates: RoomRate[];
  images: string[];
  amenities: string[];
  beds: Array<{ type?: string | null; size?: string | null; quantity?: number | null }>;
  room_size_sqm?: number | null;
  pricing: HotelPricing;
}

export interface HotelSortStat {
  hotel_id: string;
  name: string;
  fare: number;
  star_rating?: number | null;
  review_rating?: number | null;
}

export interface HotelSortStats {
  cheapest: HotelSortStat;
  top_rated: HotelSortStat;
  best_value: HotelSortStat;
}

/**
 * Filter ranges derived from THIS PAGE, not the whole destination — suppliers
 * expose no server-side rate filter, so narrowing on these narrows what we
 * already fetched.
 */
export interface HotelFacets {
  price?: { min: number; max: number } | null;
  star_ratings: number[];
  board_bases: string[];
  facilities: Array<{ name: string; count: number }>;
}

export interface HotelSearchResponse {
  provider: HotelSource | string;
  trace_id: string;
  currency: string;
  check_in: string;
  check_out: string;
  nights: number;
  destination: Destination;
  occupancies: Occupancy[];
  guest_count: GuestCount;
  nationality?: string | null;
  remaining_seconds?: number | null;
  hotels: HotelCard[];
  sort_stats?: HotelSortStats;
  facets?: HotelFacets;
  /** Only set when the provider had nothing — the API answers 200 with hotels: []. */
  detail?: string;
}

export interface HotelDetailResponse {
  provider: HotelSource | string;
  trace_id: string;
  hotel_id: string;
  currency: string;
  check_in: string;
  check_out: string;
  nights: number;
  occupancies: Occupancy[];
  guest_count: GuestCount;
  remaining_seconds?: number | null;
  hotel: HotelDetailInfo;
  rooms: RoomOffer[];
}

/** What the supplier will and won't accept. Read it; don't re-derive it. */
export interface GuestRules {
  /** How many names to collect — always one per room. */
  guests_required: number;
  one_guest_per_room: boolean;
  lead_contact_required: boolean;
  /** Hotels need NO travel documents, unlike flights. */
  documents_required: boolean;
  /** Some suppliers price a rate on every occupant being named, not just the
   *  room's lead. When true, `other_occupants` is required rather than a
   *  courtesy — the booking is rejected without it. Absent on providers that
   *  never require it. */
  all_occupants_required?: boolean;
  /** Whether a `special_requests` note can actually reach the property on this
   *  rate. When false the field is stored but goes no further, so it is better
   *  hidden than promised. */
  special_requests_supported?: boolean;
}

export interface Occupant {
  first_name: string;
  last_name?: string;
  pax_type?: "adult" | "child";
  age?: number | null;
}

/**
 * The named guest for ONE room.
 *
 * A hotel rate carries its own head count, so the supplier accepts exactly one
 * guest entry per room and rejects a second on the same `occupancy_number`.
 * Everyone else sharing that room goes in `other_occupants`, which reaches the
 * property as free text — the only channel available for them.
 */
export interface HotelGuest {
  occupancy_number?: number;
  title?: string;
  first_name: string;
  last_name: string;
  email?: string;
  contact_number?: string;
  cell_country_code?: string;
  is_lead?: boolean;
  nationality?: string;
  special_requests?: string;
  other_occupants?: Occupant[];
}

export interface HotelItinerary {
  booking_id: string;
  product?: "hotel";
  provider: HotelSource | string;
  trace_id: string;
  itinerary_code: string;
  status: BookingStatus;
  check_in: string;
  check_out: string;
  nights: number;
  occupancies: Occupancy[];
  guest_count: GuestCount;
  remaining_seconds?: number | null;
  hotel: HotelDetailInfo;
  room: RoomOffer;
  pricing: HotelPricing;
  cancellation_policies: CancellationPolicy[];
  cancellation_summary?: string | null;
  free_cancellation_until?: string | null;
  guest_rules: GuestRules;
  guests: HotelGuest[];
  // Set by /book/
  /** The SUPPLIER's booking id — NOT what the front desk recognises. */
  booking_reference?: string | null;
  supplier_booking_id?: string | null;
  /** What the property knows the stay by. Show this to the guest. */
  hotel_confirmation_code?: string | null;
  client_reference?: string | null;
  provider_status?: string | null;
  last_free_cancellation_date?: string | null;
  refundable_tag?: string | null;
  wallet_debited_inr?: number | null;
}

/** A row from GET /v1/hotels/destinations/autocomplete/. */
export interface DestinationSuggestion {
  city_name: string;
  country_code: string;
  country_name: string;
  label: string;
}

/** The snapshot on `Booking.hotel_details` — what the bookings list renders. */
export interface HotelBookingSummary {
  hotel_id?: string;
  name?: string;
  address?: string;
  city_name?: string;
  country_code?: string;
  star_rating?: number | null;
  thumbnail?: string | null;
  check_in?: string;
  check_out?: string;
  nights?: number;
  check_in_time?: string | null;
  check_out_time?: string | null;
  room_name?: string;
  board_name?: string;
  board_basis?: string;
  refundable?: boolean | null;
  free_cancellation_until?: string | null;
  hotel_confirmation_code?: string | null;
  occupancies?: Occupancy[];
  guest_count?: GuestCount;
}
